// Lightweight in-memory breadcrumb ring buffer. Captures recent user actions,
// navigation, console.error output and failed fetch/API calls. Attached to
// every error report so the Admin can see what happened just before a crash.
//
// Safety:
// - Only captured fields are URL, HTTP method, status, and short text.
// - Sensitive headers/body are never recorded. Values that look like tokens,
//   OTPs, passwords or keys are masked before storage.

export type Breadcrumb = {
  t: number; // epoch ms
  kind: "click" | "nav" | "console" | "fetch" | "action" | "supabase";
  message: string;
  data?: Record<string, unknown>;
};

const MAX = 40;
const buffer: Breadcrumb[] = [];

const SECRET_KEY_RE = /(authorization|apikey|api[-_]?key|token|secret|password|passcode|otp|pin|cvv|bearer)/i;

function maskString(s: string): string {
  if (!s) return s;
  // Mask long token-like substrings (JWTs, bearer tokens)
  return s
    .replace(/eyJ[A-Za-z0-9_\-]{10,}\.[A-Za-z0-9_\-]{5,}\.[A-Za-z0-9_\-]{5,}/g, "[jwt:masked]")
    .replace(/sb_(secret|publishable)_[A-Za-z0-9_-]+/g, "[sb-key:masked]")
    .replace(/\b\d{4,8}\b(?=.*(otp|pin|code))/gi, "[otp:masked]");
}

export function maskObject(v: unknown, depth = 0): unknown {
  if (v == null || depth > 4) return v;
  if (typeof v === "string") return maskString(v).slice(0, 500);
  if (Array.isArray(v)) return v.slice(0, 20).map((x) => maskObject(x, depth + 1));
  if (typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      if (SECRET_KEY_RE.test(k)) { out[k] = "[masked]"; continue; }
      out[k] = maskObject(val, depth + 1);
    }
    return out;
  }
  return v;
}

export function addBreadcrumb(b: Omit<Breadcrumb, "t">) {
  buffer.push({ t: Date.now(), ...b, message: maskString(String(b.message)).slice(0, 300), data: b.data ? (maskObject(b.data) as Record<string, unknown>) : undefined });
  if (buffer.length > MAX) buffer.splice(0, buffer.length - MAX);
}

export function getBreadcrumbs(): Breadcrumb[] {
  return buffer.slice();
}

export function clearBreadcrumbs() {
  buffer.length = 0;
}

let installed = false;
export function installBreadcrumbs() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // clicks — record best-effort target label
  window.addEventListener(
    "click",
    (ev) => {
      const t = ev.target as HTMLElement | null;
      if (!t) return;
      const btn = t.closest("button, a, [role=button], input, select, textarea") as HTMLElement | null;
      if (!btn) return;
      const label =
        btn.getAttribute("aria-label") ||
        (btn as HTMLInputElement).name ||
        btn.textContent?.trim().slice(0, 60) ||
        btn.tagName.toLowerCase();
      addBreadcrumb({ kind: "click", message: label || btn.tagName });
    },
    { capture: true, passive: true },
  );

  // navigation via history API
  const push = history.pushState.bind(history);
  const replace = history.replaceState.bind(history);
  history.pushState = function (...args: Parameters<typeof push>) {
    addBreadcrumb({ kind: "nav", message: `push ${String(args[2] ?? location.pathname)}` });
    return push(...args);
  } as typeof history.pushState;
  history.replaceState = function (...args: Parameters<typeof replace>) {
    addBreadcrumb({ kind: "nav", message: `replace ${String(args[2] ?? location.pathname)}` });
    return replace(...args);
  } as typeof history.replaceState;
  window.addEventListener("popstate", () =>
    addBreadcrumb({ kind: "nav", message: `pop ${location.pathname}` }),
  );

  // console.error mirror
  const origErr = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try {
      const msg = args
        .map((a) => (a instanceof Error ? `${a.name}: ${a.message}` : typeof a === "string" ? a : safeJson(a)))
        .join(" ");
      addBreadcrumb({ kind: "console", message: msg });
    } catch {}
    origErr(...args);
  };

  // fetch wrapper — records failures + non-2xx responses only
  if (typeof window.fetch === "function") {
    const origFetch = window.fetch.bind(window);
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const started = Date.now();
      const method = (init?.method || (typeof input !== "string" && "method" in (input as Request) ? (input as Request).method : "GET")).toUpperCase();
      const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : (input as Request).url;
      try {
        const res = await origFetch(input as RequestInfo, init);
        if (!res.ok) {
          addBreadcrumb({
            kind: "fetch",
            message: `${method} ${shortUrl(url)} → ${res.status}`,
            data: { url: shortUrl(url), method, status: res.status, ms: Date.now() - started },
          });
        }
        return res;
      } catch (e) {
        addBreadcrumb({
          kind: "fetch",
          message: `${method} ${shortUrl(url)} → network error`,
          data: { url: shortUrl(url), method, error: e instanceof Error ? e.message : String(e) },
        });
        throw e;
      }
    };
  }
}

function safeJson(v: unknown): string {
  try { return JSON.stringify(maskObject(v)); } catch { return "[unserializable]"; }
}

function shortUrl(u: string): string {
  try {
    const url = new URL(u, typeof location !== "undefined" ? location.href : "http://x");
    return `${url.pathname}${url.search ? "?" + url.searchParams.toString().slice(0, 120) : ""}`;
  } catch {
    return String(u).slice(0, 200);
  }
}
